import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Tooltip } from "antd";
import {
  LogoutOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import primioLogo from "./primio_logo.png";
import primioLogoDark from "./primio_logo_dark.png";
import { CheckedEnvironment } from "./types";
import {
  ENVIRONMENT_HEALTH_FILTERS,
  EnvironmentHealthFilter,
  UNKNOWN_API_VERSION,
  countEnvironmentsByHealth,
  deriveTenantStatus,
  environmentMatchesHealth,
  environmentMatchesQuery,
  groupEnvironmentsByApiVersion,
  latestApiVersion,
  shouldShowQuietVersionSummary,
  sortOnName,
} from "./utils";
import { Check, ExclamationMark, HorizontalLine } from "./icons";
import { useAuth } from "./auth/AuthContext";
import { AuthForms } from "./auth/AuthForms";
import { resolveAuthGatePhase } from "./auth/authGatePhase";
import {
  AUTH_REASON_PARAM,
  authReasonMessageKey,
  buildLoginRedirectSearch,
  hasOAuthCallbackParams,
  resolveAuthReason,
  stripAuthRedirectParams,
} from "./auth/authRedirect";
import { ThemeToggle } from "./theme/ThemeToggle";
import { useTheme } from "./theme/ThemeProvider";
import { LanguageToggle } from "./i18n/LanguageToggle";
import { toBcp47Locale } from "./i18n";
import { CheckedMobileApp, StoreAppInfo } from "./mobile-apps/types";
import { formatStoreDate } from "./mobile-apps/format-store-date";
import {
  REFRESH_INTERVAL_OPTIONS,
  RefreshIntervalMs,
} from "./refresh-interval";
import { applyStatusFavicon } from "./status-favicon";
import { useStatusRefresh, withStatusRefresh } from "./status-refresh";
import { DevScenarioPanel } from "./mock/DevScenarioPanel";
import { clearMockScenario, isMockModeActive } from "./mock/mock-mode";
import Strands from "./components/Strands";
import { useTranslation } from "react-i18next";

const OVERALL_STRAND_COLORS = {
  good: ["#0bb86e", "#34d399", "#06b6d4", "#22c55e"],
  warning: ["#0bb86e", "#f6bb56", "#ff4d4f", "#eab308"],
  error: ["#ff4d4f", "#f43f5e", "#fb7185", "#ef4444"],
  empty: ["#94a3b8", "#64748b", "#cbd5e1", "#6b7280"],
} as const;

const OVERALL_STRAND_COLORS_LIGHT = {
  good: ["#047857", "#0bb86e", "#0891b2", "#059669"],
  warning: ["#047857", "#d97706", "#dc2626", "#ca8a04"],
  error: ["#b91c1c", "#e11d48", "#be123c", "#dc2626"],
  empty: ["#64748b", "#475569", "#94a3b8", "#4b5563"],
} as const;

function PrimioLogo({ className, alt }: { className?: string; alt?: string }) {
  const { resolved } = useTheme();
  const { t } = useTranslation();
  return (
    <img
      alt={alt ?? t("brand.logoAlt")}
      src={resolved === "dark" ? primioLogoDark : primioLogo}
      className={className}
    />
  );
}

function AppFabStack() {
  return (
    <div className="App-fabStack">
      <ThemeToggle />
      <LanguageToggle />
    </div>
  );
}

function App() {
  if (isMockModeActive()) {
    return <StatusApp isMock />;
  }

  return <AuthGatedApp />;
}

/** Shared chrome for login / loading / sign-out so auth transitions don't flash. */
function AuthLanding({
  children,
  staticMotion = false,
}: {
  children?: React.ReactNode;
  /** Skip rise animations during transient auth states (load / sign-out). */
  staticMotion?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={
        "App Auth-screen" + (staticMotion ? " Auth-screen--static" : "")
      }
    >
      <div className="App-backdrop" aria-hidden />
      <div className="Auth-screen__content">
        <PrimioLogo alt={t("brand.name")} className="Auth-screen__brand" />
        <p className="Auth-screen__lede">{t("auth.lede")}</p>
        {children}
      </div>
      <AppFabStack />
      <DevScenarioPanel />
    </div>
  );
}

function AuthGatedApp() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSigningOut, setIsSigningOut] = useState(false);

  // After sign-in, drop leftover login-reason / OAuth callback params and land on the app.
  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) return;
    const params = new URLSearchParams(location.search);
    const shouldStrip =
      params.has(AUTH_REASON_PARAM) ||
      hasOAuthCallbackParams(params) ||
      location.pathname === "/";
    if (!shouldStrip) return;
    stripAuthRedirectParams(params);
    const search = params.toString();
    const pathname =
      location.pathname === "/" ? "/environments" : location.pathname;
    navigate(
      { pathname, search: search ? `?${search}` : "" },
      { replace: true },
    );
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Unauthenticated visits to /apps or /environments (or any non-login path)
  // land on `/` with ?auth= so the login page can show a message.
  useEffect(() => {
    if (auth.isLoading || isSigningOut || auth.isAuthenticated) return;
    if (location.pathname === "/") return;
    const reason = resolveAuthReason(Boolean(auth.error));
    navigate(
      {
        pathname: "/",
        search: buildLoginRedirectSearch(location.search, reason),
      },
      { replace: true },
    );
  }, [
    auth.error,
    auth.isAuthenticated,
    auth.isLoading,
    isSigningOut,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Keep the landing chrome painted while Amplify initialises / sign-out runs
  // so we never flash a spinner / blank frame.
  const phase = resolveAuthGatePhase({
    isSigningOut,
    isLoading: auth.isLoading,
    isAuthenticated: auth.isAuthenticated,
  });

  const loginReasonKey = authReasonMessageKey(
    new URLSearchParams(location.search).get(AUTH_REASON_PARAM),
  );

  if (phase.kind === "landing") {
    if (phase.content === "signin") {
      return (
        <AuthLanding staticMotion={phase.staticMotion}>
          <AuthForms loginReasonKey={loginReasonKey} />
        </AuthLanding>
      );
    }

    return <AuthLanding staticMotion={phase.staticMotion} />;
  }

  const handleSignOut = () => {
    setIsSigningOut(true);
    void auth
      .signOut()
      .catch(() => undefined)
      .finally(() => {
        // OAuth sign-out sets window.location to Cognito /logout; do not SPA-navigate here.
        setIsSigningOut(false);
      });
  };

  return <StatusApp isMock={false} onSignOut={handleSignOut} />;
}

function StatusAppShell({
  isMock,
  onSignOut,
}: {
  isMock: boolean;
  onSignOut?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = toBcp47Locale(i18n.language);
  const {
    environments,
    mobileApps,
    lastCheckedDate,
    checkGeneration,
    refreshIntervalMs,
    setRefreshIntervalMs,
    refresh,
  } = useStatusRefresh();

  useEffect(() => {
    const status = deriveOverallStatus(environments);
    document.title =
      environments.length === 0
        ? t("status.documentTitle")
        : overallStatusTitle(t, status, environments);
    applyStatusFavicon(status, environments.length > 0);
  }, [environments, t, i18n.language]);

  const exitMock = () => {
    clearMockScenario();
    window.location.reload();
  };

  return (
    <div className="App Status-screen">
      <div className="App-backdrop" aria-hidden />
      <div className="App-header">
        <div className="Container">
          <PrimioLogo className="App-logo" />
          <div className="Divider-vertical" />
          <nav className="App-nav" aria-label={t("nav.ariaLabel")}>
            <NavLink
              to="/environments"
              className={({ isActive }) =>
                "App-navLink" + (isActive ? " App-navLink--active" : "")
              }
            >
              {t("nav.environments")}
            </NavLink>
            <NavLink
              to="/apps"
              className={({ isActive }) =>
                "App-navLink" + (isActive ? " App-navLink--active" : "")
              }
            >
              {t("nav.apps")}
            </NavLink>
          </nav>
          <div className="Spacer" style={{ width: "100%" }} />
          <div className="App-headerRefresh">
            {lastCheckedDate && (
              <time
                className="Overview-checked Overview-checked--header"
                dateTime={lastCheckedDate.toISOString()}
              >
                <span className="Overview-checkedLabel">
                  {t("header.checked")}
                </span>
                <span className="Overview-checkedValue">
                  {lastCheckedDate.toLocaleTimeString(locale)}
                </span>
              </time>
            )}
            <div
              className="Overview-refresh"
              role="group"
              aria-label={t("header.refresh")}
            >
              <Select
                className="Overview-intervalSelect"
                variant="borderless"
                value={refreshIntervalMs}
                options={REFRESH_INTERVAL_OPTIONS.map((option) => ({
                  value: option.value,
                  label:
                    option.value === 0
                      ? t("header.manual")
                      : t("header.every", { interval: t(option.labelKey) }),
                }))}
                onChange={(value) =>
                  setRefreshIntervalMs(value as RefreshIntervalMs)
                }
                popupMatchSelectWidth={false}
                aria-label={t("header.refreshInterval")}
              />
              <span className="Overview-refreshDivider" aria-hidden />
              <Tooltip title={t("header.refreshNow")}>
                <button
                  type="button"
                  className="Overview-refreshBtn"
                  aria-label={t("header.refreshNow")}
                  onClick={refresh}
                >
                  <ReloadOutlined />
                </button>
              </Tooltip>
            </div>
          </div>
          {isMock ? (
            <button
              type="button"
              className="App-headerAction"
              onClick={exitMock}
            >
              {t("header.exitMock")}
            </button>
          ) : (
            <button
              type="button"
              className="App-headerAction"
              onClick={onSignOut}
            >
              <LogoutOutlined aria-hidden />
              {t("header.signOut")}
            </button>
          )}
        </div>
      </div>
      <div className="Container App-content">
        <Routes>
          <Route
            path="/environments"
            element={
              <EnvironmentsPage
                environments={environments}
                checkGeneration={checkGeneration}
              />
            }
          />
          <Route path="/apps" element={<MobileOverview apps={mobileApps} />} />
          <Route path="/" element={<RedirectToEnvironments />} />
          <Route path="*" element={<Navigate to="/environments" replace />} />
        </Routes>
      </div>
      <AppFabStack />
      <DevScenarioPanel onScenarioChange={isMock ? refresh : undefined} />
    </div>
  );
}

const StatusApp = withStatusRefresh(StatusAppShell);

/** Keep `?mock=` when sending `/` → `/environments`, but drop login / OAuth params. */
function RedirectToEnvironments() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  stripAuthRedirectParams(params);
  const search = params.toString();
  return (
    <Navigate
      to={search ? `/environments?${search}` : "/environments"}
      replace
    />
  );
}

function EnvironmentsPage({
  environments,
  checkGeneration,
}: {
  environments: CheckedEnvironment[];
  checkGeneration: number;
}) {
  return (
    <div className="EnvironmentsPage">
      <OverallStatus environments={environments} />
      <Overview environments={environments} checkGeneration={checkGeneration} />
    </div>
  );
}

function deriveOverallStatus(
  environments: CheckedEnvironment[],
): "good" | "warning" | "error" {
  const unhealthy = environments.filter(
    (e) => e.status === "offline" || e.cmsStatus === "offline",
  );
  if (unhealthy.length === 0) {
    return "good";
  }

  if (
    environments.length > 0 &&
    environments.every(
      (e) => e.status === "offline" && e.cmsStatus === "offline",
    )
  ) {
    return "error";
  }

  return "warning";
}

function overallStatusTitle(
  t: (key: string) => string,
  status: "good" | "warning" | "error",
  environments?: CheckedEnvironment[],
) {
  if (environments && environments.length === 0) {
    return t("status.empty");
  }
  if (status === "good") return t("status.good");
  if (status === "warning") return t("status.warning");
  return t("status.error");
}

function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="EmptyState" role="status">
      <p className="EmptyState-title">{title}</p>
      <p className="EmptyState-description">{description}</p>
      {action}
    </div>
  );
}

function OverallStatus({
  environments,
}: {
  environments: CheckedEnvironment[];
}) {
  const { t } = useTranslation();
  const { resolved: theme } = useTheme();
  const status = deriveOverallStatus(environments);
  const title = overallStatusTitle(t, status, environments);
  const palette =
    theme === "light" ? OVERALL_STRAND_COLORS_LIGHT : OVERALL_STRAND_COLORS;
  const strandColors =
    environments.length === 0 ? palette.empty : palette[status];
  const [titleOpacity, setTitleOpacity] = useState(1);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );
  const [strandsReady, setStrandsReady] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => {
      setReduceMotion(media.matches);
      if (media.matches) setStrandsReady(false);
    };
    onChange();
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        setTitleOpacity(Math.max(0, Math.min(1, 1 - y / 140)));
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <>
      <div
        className={`Overall Overall--v2 Overall--fixed Overall--${status}${
          strandsReady ? " Overall--strands-ready" : ""
        }`}
      >
        <div className="Overall-indicator" aria-hidden>
          <div className="Overall-indicator-canvas">
            {!reduceMotion && (
              <Strands
                colors={[...strandColors]}
                count={5}
                speed={0.2}
                amplitude={0.9}
                waviness={1.5}
                thickness={1.7}
                glow={theme === "light" ? 0.75 : 1.15}
                taper={6}
                spread={0.8}
                hueShift={0}
                intensity={0.35}
                saturation={2}
                opacity={1}
                scale={1}
                onReady={() => setStrandsReady(true)}
                onError={() => setStrandsReady(false)}
              />
            )}
          </div>
        </div>
        <div
          className={`Overall-icon Overall-icon--fallback ${status}`}
          aria-hidden
        >
          {status === "good" && <Check />}
          {status === "warning" && <ExclamationMark />}
          {status === "error" && <HorizontalLine />}
        </div>
        <h1 className="Overall-title" style={{ opacity: titleOpacity }}>
          {title}
        </h1>
      </div>
      <div className="Overall-spacer" aria-hidden />
    </>
  );
}

function Overview({
  environments,
  checkGeneration,
}: {
  environments: CheckedEnvironment[];
  checkGeneration: number;
}) {
  const { t } = useTranslation();
  // Detect generation changes while this page is mounted. Remounting
  // Environments (e.g. Apps → Environments) must not replay the last pulse.
  const prevGenerationRef = useRef(checkGeneration);
  const [pulseKey, setPulseKey] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [health, setHealth] = useState<EnvironmentHealthFilter>("all");

  useEffect(() => {
    if (checkGeneration === prevGenerationRef.current) return;
    prevGenerationRef.current = checkGeneration;
    if (checkGeneration > 0) {
      setPulseKey(checkGeneration);
    }
  }, [checkGeneration]);

  const queryMatched = useMemo(
    () =>
      environments.filter((environment) =>
        environmentMatchesQuery(environment, query),
      ),
    [environments, query],
  );
  const healthCounts = useMemo(
    () => countEnvironmentsByHealth(queryMatched),
    [queryMatched],
  );
  const visibleEnvironments = useMemo(
    () =>
      queryMatched
        .filter((environment) => environmentMatchesHealth(environment, health))
        .sort(sortOnName),
    [health, queryMatched],
  );
  const fleetLatest = useMemo(
    () => latestApiVersion(environments),
    [environments],
  );
  const versionGroups = useMemo(
    () => groupEnvironmentsByApiVersion(visibleEnvironments, fleetLatest),
    [visibleEnvironments, fleetLatest],
  );
  const quietVersionSummary = shouldShowQuietVersionSummary(
    versionGroups,
    fleetLatest,
  );
  const hasActiveFilters = health !== "all" || query.trim() !== "";

  const clearFilters = () => {
    setQuery("");
    setHealth("all");
  };

  let pulseIndex = 0;

  return (
    <section className="Overview">
      <header className="Overview-header Overview-header--split">
        <div>
          <span className="Overview-kicker">{t("environments.kicker")}</span>
          <h2 className="Overview-title">{t("environments.title")}</h2>
        </div>
        {environments.length > 0 && (
          <div className="Overview-actions">
            <label className="Overview-search">
              <SearchOutlined aria-hidden />
              <span className="VisuallyHidden">{t("environments.search")}</span>
              <input
                className="Overview-searchInput"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("environments.searchPlaceholder")}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <div
              className="Overview-filterChips"
              role="radiogroup"
              aria-label={t("environments.filterAria")}
            >
              {ENVIRONMENT_HEALTH_FILTERS.map((id) => {
                const count = healthCounts[id];
                const isActive = health === id;
                const alertCount = id === "issues" && count > 0;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    data-health={id}
                    className={
                      "Overview-filterChip" +
                      (isActive ? " Overview-filterChip--active" : "") +
                      (id === "issues" ? " Overview-filterChip--issues" : "")
                    }
                    onClick={() => setHealth(id)}
                  >
                    {t(`environments.filter.${id}`)}
                    <span
                      className={
                        "Overview-filterCount" +
                        (alertCount ? " Overview-filterCount--alert" : "")
                      }
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </header>
      {environments.length === 0 ? (
        <EmptyState
          title={t("environments.emptyTitle")}
          description={t("environments.emptyDescription")}
        />
      ) : visibleEnvironments.length === 0 ? (
        <EmptyState
          title={t("environments.filterEmptyTitle")}
          description={t("environments.filterEmptyDescription")}
          action={
            hasActiveFilters ? (
              <button
                type="button"
                className="Overview-clearFilters"
                onClick={clearFilters}
              >
                {t("environments.clearFilters")}
              </button>
            ) : undefined
          }
        />
      ) : quietVersionSummary ? (
        <>
          {fleetLatest && (
            <p className="Overview-versionSummary">
              {t("environments.version.allOn", {
                count: visibleEnvironments.length,
                version: fleetLatest,
              })}
            </p>
          )}
          <div className="Overview-body">
            {visibleEnvironments.map((environment) => {
              const index = pulseIndex;
              pulseIndex += 1;
              return (
                <TenantNode
                  environment={environment}
                  key={environment.organisation}
                  pulseKey={pulseKey}
                  pulseIndex={index}
                />
              );
            })}
          </div>
        </>
      ) : (
        <div className="Overview-groups">
          {versionGroups.map((group) => {
            const label =
              group.version === UNKNOWN_API_VERSION
                ? t("environments.version.unknown")
                : group.version;
            return (
              <section
                className="Overview-group"
                key={group.version}
                data-version={group.version}
              >
                <header className="Overview-groupHeader">
                  <h3 className="Overview-groupTitle">{label}</h3>
                  <span className="Overview-filterCount">
                    {group.environments.length}
                  </span>
                  {group.behind && (
                    <span className="Overview-versionBehind">
                      {t("environments.version.behind")}
                    </span>
                  )}
                </header>
                <div className="Overview-body">
                  {group.environments.map((environment) => {
                    const index = pulseIndex;
                    pulseIndex += 1;
                    return (
                      <TenantNode
                        environment={environment}
                        key={environment.organisation}
                        pulseKey={pulseKey}
                        pulseIndex={index}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

function TenantNode({
  environment,
  pulseKey,
  pulseIndex,
}: {
  environment: CheckedEnvironment;
  pulseKey: number | null;
  pulseIndex: number;
}) {
  const { t } = useTranslation();
  const { status, cmsStatus, version, organisation } = environment;
  const domain = ".primio.app";
  const apiDocsUrl = `https://api.${organisation}${domain}/docs`;
  const cmsUrl = `https://cms.${organisation}${domain}`;
  const tenantStatus = deriveTenantStatus(environment);

  return (
    <article className={`TenantNode TenantNode--${tenantStatus}`}>
      {pulseKey !== null && (
        <span
          key={pulseKey}
          className="TenantNode-pulse"
          style={
            {
              ["--pulse-delay" as string]: `${pulseIndex * 140}ms`,
            } as React.CSSProperties
          }
          aria-hidden
        />
      )}
      <div className="TenantNode-hub">
        <span className="TenantNode-hubDot" aria-hidden />
        <h3 className="TenantNode-name">{organisation}</h3>
      </div>

      <div className="TenantNode-rail" aria-hidden>
        <span className="TenantNode-railStem" />
        <div className="TenantNode-railFork">
          <span className="TenantNode-railDrop" />
          <span className="TenantNode-railDrop" />
        </div>
      </div>

      <div className="TenantNode-services">
        <a
          className={`ServiceNode ServiceNode--${status}`}
          href={apiDocsUrl}
          target="_blank"
          rel="noreferrer"
        >
          <span className="ServiceNode-status" aria-hidden />
          <span className="ServiceNode-meta">
            <span className="ServiceNode-kind">{t("environments.api")}</span>
            <span className="ServiceNode-detail">
              {t(`environments.${status}`)}
              {version ? ` · ${version}` : ""}
            </span>
          </span>
        </a>

        <a
          className={`ServiceNode ServiceNode--${cmsStatus}`}
          href={cmsUrl}
          target="_blank"
          rel="noreferrer"
        >
          <span className="ServiceNode-status" aria-hidden />
          <span className="ServiceNode-meta">
            <span className="ServiceNode-kind">{t("environments.cms")}</span>
            <span className="ServiceNode-detail">
              {t(`environments.${cmsStatus}`)}
            </span>
          </span>
        </a>
      </div>
    </article>
  );
}

function MobileOverview({ apps }: { apps: CheckedMobileApp[] }) {
  const { t } = useTranslation();
  return (
    <section className="Overview Overview--page">
      <header className="Overview-header">
        <span className="Overview-kicker">{t("apps.kicker")}</span>
        <h2 className="Overview-title">{t("apps.title")}</h2>
      </header>
      {apps.length === 0 ? (
        <EmptyState
          title={t("apps.emptyTitle")}
          description={t("apps.emptyDescription")}
        />
      ) : (
        <div className="Overview-body">
          {apps.map((app) => (
            <MobileAppNode app={app} key={app.id} />
          ))}
        </div>
      )}
    </section>
  );
}

function MobileAppNode({ app }: { app: CheckedMobileApp }) {
  const { t } = useTranslation();
  const platforms = [app.ios, app.android].filter(Boolean);
  const overallStatus = platforms.every((s) => s?.status === "online")
    ? "online"
    : platforms.every((s) => s?.status === "offline")
    ? "offline"
    : "mixed";

  const iconUrl = app.ios?.iconUrl || app.android?.iconUrl;

  return (
    <article className={`TenantNode TenantNode--${overallStatus}`}>
      <div className="TenantNode-hub">
        {iconUrl ? (
          <img
            className="TenantNode-hubIcon"
            src={iconUrl}
            alt=""
            width={28}
            height={28}
          />
        ) : (
          <span className="TenantNode-hubDot" aria-hidden />
        )}
        <h3 className="TenantNode-name">{app.name}</h3>
      </div>

      <div className="TenantNode-rail" aria-hidden>
        <span className="TenantNode-railStem" />
        <div className="TenantNode-railFork">
          {app.ios && <span className="TenantNode-railDrop" />}
          {app.android && <span className="TenantNode-railDrop" />}
        </div>
      </div>

      <div className="TenantNode-services">
        {app.ios && <StoreNode info={app.ios} label={t("apps.ios")} />}
        {app.android && (
          <StoreNode info={app.android} label={t("apps.android")} />
        )}
      </div>
    </article>
  );
}

function StoreNode({ info, label }: { info: StoreAppInfo; label: string }) {
  const { t, i18n } = useTranslation();
  const locale = toBcp47Locale(i18n.language);
  const className = `ServiceNode ServiceNode--${info.status}`;
  const content = (
    <>
      <span className="ServiceNode-status" aria-hidden />
      <span className="ServiceNode-meta">
        <span className="ServiceNode-kind">{label}</span>
        {info.status === "online" ? (
          <>
            {info.version && (
              <span className="ServiceNode-detail ServiceNode-detail--primary">
                {info.version}
              </span>
            )}
            {info.lastUpdated && (
              <span className="ServiceNode-detail">
                {t("apps.updated", {
                  date: formatStoreDate(info.lastUpdated, locale),
                })}
              </span>
            )}
          </>
        ) : (
          <span className="ServiceNode-detail">
            {info.error || t("apps.unavailable")}
          </span>
        )}
      </span>
    </>
  );

  if (info.storeUrl) {
    return (
      <a
        className={className}
        href={info.storeUrl}
        target="_blank"
        rel="noreferrer"
      >
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
}

export default App;
